/**
 * Xero Integration API Service
 */

import apiClient from './client';

export interface XeroConnection {
  id: string;
  tenant_id: string;
  xero_tenant_id: string;
  xero_tenant_name: string;
  xero_tenant_type: string;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string;
  last_sync_error: string | null;
}

export interface XeroConnectionStatus {
  connected: boolean;
  connectedBy?: string;
  connectedAt?: string;
  organizations: XeroConnection[];
  status?: 'active' | 'error';
  errorMessage?: string;
}

export interface XeroSyncLog {
  id: string;
  sync_type: string;
  status: string;
  items_processed: number;
  items_total: number | null;
  items_created: number;
  items_updated: number;
  items_failed: number;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  sync_summary: Record<string, any>;
}

export interface XeroContact {
  ContactID: string;
  Name: string;
  EmailAddress?: string;
  TaxNumber?: string;
  IsCustomer: boolean;
  IsSupplier: boolean;
}

export interface ClientMapping {
  id: string;
  client_code: string;
  display_name: string;
  abn: string | null;
  email: string | null;
  xero_contact_id: string | null;
  xero_contact_name: string | null;
  xero_last_synced_at: string | null;
}

export async function getConnectionStatus(): Promise<XeroConnectionStatus> {
  const response = await apiClient.get<{ success: boolean; data: XeroConnectionStatus }>(
    '/integrations/xero/status'
  );
  return response.data.data;
}

export async function connect(returnUrl?: string, clientId?: string): Promise<{ authUrl: string }> {
  const params = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
  const response = await apiClient.post<{ success: boolean; data: { authUrl: string } }>(
    `/integrations/xero/connect${params}`,
    clientId ? { clientId } : undefined
  );
  return response.data.data;
}

export async function disconnect(xeroTenantId?: string): Promise<void> {
  const params = xeroTenantId ? `?xeroTenantId=${encodeURIComponent(xeroTenantId)}` : '';
  await apiClient.delete(`/integrations/xero/disconnect${params}`);
}

export async function triggerSync(xeroConnectionId?: string, syncType?: string): Promise<{ syncId: string }> {
  const response = await apiClient.post<{ success: boolean; data: { syncId: string } }>(
    '/integrations/xero/sync',
    { xeroConnectionId, syncType }
  );
  return response.data.data;
}

export async function getSyncStatus(): Promise<XeroSyncLog[]> {
  const response = await apiClient.get<{ success: boolean; data: XeroSyncLog[] }>(
    '/integrations/xero/sync-status'
  );
  return response.data.data;
}

export async function getContacts(search?: string): Promise<{ contacts: XeroContact[]; total: number; requiresSearch?: boolean }> {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await apiClient.get<{ success: boolean; data: { contacts: XeroContact[]; total: number; requiresSearch?: boolean } }>(
    `/integrations/xero/contacts${params}`
  );
  return response.data.data;
}

export async function getClientMappings(): Promise<ClientMapping[]> {
  const response = await apiClient.get<{ success: boolean; data: ClientMapping[] }>(
    '/integrations/xero/client-mappings'
  );
  return response.data.data;
}

export async function mapClient(clientId: string, xeroContactId: string | null, xeroContactName?: string): Promise<void> {
  await apiClient.put(`/integrations/xero/clients/${clientId}/map`, {
    xeroContactId,
    xeroContactName,
  });
}

// ==================== Suggest Mappings ====================

export interface SuggestedMapping {
  clientId: string;
  clientDisplayName: string;
  clientCode: string;
  suggestedContact: {
    ContactID: string;
    Name: string;
    EmailAddress?: string;
    TaxNumber?: string;
  };
  confidence: 'high' | 'medium' | 'low';
  matchReason: string;
}

export async function getSuggestedMappings(): Promise<SuggestedMapping[]> {
  const response = await apiClient.get<{ success: boolean; data: SuggestedMapping[] }>(
    '/integrations/xero/suggest-mappings'
  );
  return response.data.data;
}

export async function remapByAbn(): Promise<{ mapped: number; total: number }> {
  const response = await apiClient.post<{ success: boolean; data: { mapped: number; total: number } }>(
    '/integrations/xero/remap-by-abn'
  );
  return response.data.data;
}

// ==================== Settings (Super Admin Only) ====================

export interface XeroSetting {
  key: string;
  isConfigured: boolean;
  isActive: boolean;
  maskedValue?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export async function getXeroSettings(): Promise<XeroSetting[]> {
  const response = await apiClient.get<{ success: boolean; data: XeroSetting[] }>(
    '/integrations/xero/settings'
  );
  return response.data.data;
}

export async function setXeroSetting(key: string, value: string): Promise<void> {
  await apiClient.put('/integrations/xero/settings', { key, value });
}

export async function deleteXeroSetting(key: string): Promise<void> {
  await apiClient.delete(`/integrations/xero/settings/${key}`);
}
